import React, { useState, useEffect } from 'react';
import { onAuthStateChanged } from "firebase/auth";
import { collection, addDoc, query, where, getDocs, doc, getDoc, updateDoc } from "firebase/firestore";
import { auth, db } from '../firebaseConfig/firebase';
import { Container, Form as BootstrapForm, Button, Spinner, Alert } from 'react-bootstrap';
import { Formik, Field, Form as FormikForm, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import Login from './Login';
import Header from './Header';

const opcionesPrioridad = ["GUION", "DIRECCIÓN DE ARTE", "DIRECCIÓN DE FICCIÓN", "DIRECCIÓN DE NO FICCIÓN", "DIRECCIÓN DE FOTOGRAFIA", "MONTAJE", "PRODUCCIÓN", "SONIDO"];


const Form = () => {
    const [user, setUser] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(null);
    const [isFormActive, setIsFormActive] = useState(true);
    const [isPortfolioActive, setIsPortfolioActive] = useState(false);
    const [isUpdateActive, setIsUpdateActive] = useState(false); // NUEVO: Estado para controlar el campo nuevaUrl
    const [submittedData, setSubmittedData] = useState(null);
    const [loadingData, setLoadingData] = useState(true);
    const [alertConfig, setAlertConfig] = useState({ show: false, message: '', variant: 'success' });

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            setIsAuthenticated(!!currentUser);
            setUser(currentUser);

            if (currentUser) {
                try {
                    // 1. Consultar estado de los campos de control
                    const docControlRef = doc(db, "tribu-form-control", "control");
                    const docControlSnap = await getDoc(docControlRef);
                    if (docControlSnap.exists()) {
                        const data = docControlSnap.data();
                        setIsFormActive(data.activo);
                        setIsPortfolioActive(data.portfoli_activo === true);
                        setIsUpdateActive(data.update === true); // NUEVO: Leemos el campo update
                    }

                    // 2. Consultar si el usuario ya tiene un registro
                    await fetchSubmittedData(currentUser.email);

                } catch (error) {
                    console.error("Error inicializando datos:", error);
                } finally {
                    setLoadingData(false);
                }
            } else {
                setLoadingData(false);
            }
        });

        return () => unsubscribe();
    }, []);

    const fetchSubmittedData = async (email) => {
        const q = query(collection(db, "tribu-form"), where("EMAIL_ALUMNO", "==", email));
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
            const docSnap = querySnapshot.docs[0];
            setSubmittedData({ id: docSnap.id, ...docSnap.data() });
        }
    };

    const handleSubmit = async (values, { resetForm, setSubmitting }) => {
        setAlertConfig({ show: false });
        try {
            const formData = {
                ALUMNO: user?.displayName || "Usuario sin nombre",
                EMAIL_ALUMNO: user?.email,
                PRIORIDAD_1: values.prioridad_1,
                PRIORIDAD_2: values.prioridad_2,
                PRIORIDAD_3: values.prioridad_3,
                TELEFONO: values.telefono,
                FOUNDATION: values.cursado_foundation,
                fechaCreacion: new Date()
            };

            await addDoc(collection(db, "tribu-form"), formData);

            setAlertConfig({ show: true, message: "¡Inscripción enviada con éxito!", variant: 'success' });
            await fetchSubmittedData(user.email);
            resetForm();
        } catch (error) {
            setAlertConfig({ show: true, message: "Error al guardar.", variant: 'danger' });
        } finally {
            setSubmitting(false);
        }
    };

    // MODIFICADO: Ahora esta función maneja la actualización de cualquier campo extra (portfolio o nuevaUrl)
    const handleExtraFieldsUpdate = async (values, { setSubmitting }) => {
        setAlertConfig({ show: false });
        try {
            const docRef = doc(db, "tribu-form", submittedData.id);
            const updateData = {};

            if (isPortfolioActive && values.portfolio) {
                updateData.PORTFOLIO = values.portfolio;
            }
            if (isUpdateActive && values.nuevaUrl) {
                updateData.PORTFOLIO_UPDATE = values.nuevaUrl;
                updateData.COMENTARIOS = values.comentarios;
            }

            await updateDoc(docRef, updateData);

            setAlertConfig({ show: true, message: "¡Datos guardados con éxito!", variant: 'success' });
            await fetchSubmittedData(user.email);
        } catch (error) {
            setAlertConfig({ show: true, message: "Error al actualizar los datos.", variant: 'danger' });
        } finally {
            setSubmitting(false);
        }
    };

    if (isAuthenticated === null || loadingData) return <div className="vh-100 d-flex justify-content-center align-items-center"><Spinner animation="border" /></div>;
    if (!isAuthenticated) return <Login />;

    return (
        <>
            <Container className="mt-5 mb-5">
                <div className="p-4 shadow rounded bg-white">
                    <h2>Inscripción Especialidad</h2>
                    <p className="text-muted">Usuario: <strong>{user?.email}</strong></p>
                    <hr />

                    {alertConfig.show && (
                        <Alert variant={alertConfig.variant} onClose={() => setAlertConfig({ ...alertConfig, show: false })} dismissible>
                            {alertConfig.message}
                        </Alert>
                    )}

                    {/* --- SECCIÓN DE DATOS ACTUALES --- */}
                    {submittedData && (
                        <div className="mb-4 p-3 border rounded bg-light">
                            <h5 className="text-primary mb-3">Tus datos registrados:</h5>
                            <div className="row">
                                <div className="col-md-4 mb-2"><strong>Prioridad 1:</strong> {submittedData.PRIORIDAD_1}</div>
                                <div className="col-md-4 mb-2"><strong>Prioridad 2:</strong> {submittedData.PRIORIDAD_2}</div>
                                <div className="col-md-4 mb-2"><strong>Prioridad 3:</strong> {submittedData.PRIORIDAD_3}</div>
                                <div className="col-md-4 mb-2"><strong>Teléfono:</strong> {submittedData.TELEFONO}</div>
                            </div>

                            {/* Mostrar campos extra si ya están guardados */}
                            {(submittedData.PORTFOLIO || submittedData.PORTFOLIO_UPDATE) && <hr className="mt-2 mb-3" />}

                            {submittedData.PORTFOLIO && (
                                <div className="mb-2">
                                    <strong>Portfolio: </strong>
                                    <a href={submittedData.PORTFOLIO} target="_blank" rel="noreferrer">{submittedData.PORTFOLIO}</a>
                                </div>
                            )}

                            {/* NUEVO: Mostrar la Nueva URL si ya está guardada */}
                            {submittedData.PORTFOLIO_UPDATE && (
                                <div>
                                    <strong>Nueva URL: </strong>
                                    <a href={submittedData.PORTFOLIO_UPDATE} target="_blank" rel="noreferrer">{submittedData.PORTFOLIO_UPDATE}</a>
                                </div>
                            )}
                        </div>
                    )}

                    {!isFormActive ? (
                        <Alert variant="info" className="text-center py-4">
                            <h4>Formulario Cerrado</h4>
                            <p>No se aceptan más respuestas en este momento.</p>
                        </Alert>
                    ) : (
                        submittedData ? (
                            /* --- PASO 2: ACTUALIZACIÓN DE CAMPOS EXTRA --- */
                            (isPortfolioActive || isUpdateActive) ? (
                                <div className="mt-4 border-top pt-4">
                                    <h5 className="mb-3">Actualiza tu información adicional</h5>
                                    <p className="mb-2">
                                        Debes ingresar <strong>un solo enlace</strong> que cumpla con los siguientes requisitos:
                                    </p>
                                    <ul className="mb-3">
                                        <li>Debe comenzar con <strong>https://</strong></li>
                                        <li>Debe ser de <strong>acceso público</strong></li>
                                        <li>No debe requerir <strong>inicio de sesión ni permisos</strong></li>
                                    </ul>

                                    <Formik
                                        initialValues={{
                                            portfolio: submittedData.PORTFOLIO || '',
                                            nuevaUrl: submittedData.NUEVA_URL || '',
                                            comentarios: submittedData.COMENTARIOS || '' // 1. Nuevo valor inicial
                                        }}
                                        validationSchema={Yup.object({
                                            portfolio: Yup.string().url('Debe ser una URL válida (ej: https://tudominio.com)'),
                                            nuevaUrl: Yup.string().url('Debe ser una URL válida (ej: https://tudominio.com)'),
                                            comentarios: Yup.string().max(500, 'Máximo 500 caracteres') // 2. Validación opcional
                                        })}
                                        onSubmit={handleExtraFieldsUpdate}
                                    >
                                        {({ errors, touched, isSubmitting }) => (
                                            <FormikForm>
                                                {/* Campo Portfolio (Solo si está activo) */}
                                                {isPortfolioActive && (
                                                    <BootstrapForm.Group className="mb-3">
                                                        <BootstrapForm.Label className="fw-bold">Link del Portfolio</BootstrapForm.Label>
                                                        <Field
                                                            name="portfolio"
                                                            type="url"
                                                            placeholder="https://tu-portfolio.com"
                                                            className={`form-control ${touched.portfolio && errors.portfolio ? 'is-invalid' : ''}`}
                                                        />
                                                        <ErrorMessage name="portfolio" component="div" className="invalid-feedback" />
                                                    </BootstrapForm.Group>
                                                )}

                                                {/* Campos condicionales a isUpdateActive */}
                                                {isUpdateActive && (
                                                    <>
                                                        {/* Campo Nueva URL */}
                                                        <BootstrapForm.Group className="mb-3">
                                                            <BootstrapForm.Label className="fw-bold">Nuevo Link</BootstrapForm.Label>
                                                            <Field
                                                                name="nuevaUrl"
                                                                type="url"
                                                                placeholder="https://tu-nueva-url.com"
                                                                className={`form-control ${touched.nuevaUrl && errors.nuevaUrl ? 'is-invalid' : ''}`}
                                                            />
                                                            <ErrorMessage name="nuevaUrl" component="div" className="invalid-feedback" />
                                                        </BootstrapForm.Group>

                                                        {/* NUEVO: Campo Comentarios */}
                                                        <BootstrapForm.Group className="mb-3">
                                                            <BootstrapForm.Label className="fw-bold">Comentarios</BootstrapForm.Label>
                                                            <Field
                                                                as="textarea" // Renderiza como un textarea de HTML
                                                                rows={3}
                                                                name="comentarios"
                                                                placeholder="Escribe aquí tus observaciones..."
                                                                className={`form-control ${touched.comentarios && errors.comentarios ? 'is-invalid' : ''}`}
                                                            />
                                                            <ErrorMessage name="comentarios" component="div" className="invalid-feedback" />
                                                        </BootstrapForm.Group>
                                                    </>
                                                )}

                                                <Button variant="dark" type="submit" disabled={isSubmitting}>
                                                    {isSubmitting ? <Spinner size="sm" animation="border" /> : 'Guardar Datos'}
                                                </Button>
                                            </FormikForm>
                                        )}
                                    </Formik>
                                </div>
                            ) : (
                                <Alert variant="warning" className="text-center mt-3">
                                    Ya has enviado tu selección de prioridades. Si necesitas cambiar algo, espera al periodo de actualización.
                                </Alert>
                            )
                        ) : (
                            /* --- PASO 1: FORMULARIO ORIGINAL DE INSCRIPCIÓN --- */
                            <Formik
                                initialValues={{ prioridad_1: '', prioridad_2: '', prioridad_3: '', telefono: '', cursado_foundation: false }}
                                validationSchema={Yup.object({
                                    prioridad_1: Yup.string().required('Requerido'),
                                    prioridad_2: Yup.string().required('Requerido'),
                                    prioridad_3: Yup.string().required('Requerido'),
                                    telefono: Yup.string()
                                        .matches(/^[0-9]+$/, "Solo se permiten números")
                                        .min(9, "Debe tener al menos 9 dígitos")
                                        .required('El teléfono es requerido'),
                                    cursado_foundation: Yup.boolean()
                                })}
                                onSubmit={handleSubmit}
                            >
                                {({ errors, touched, isSubmitting }) => (
                                    <FormikForm>
                                        {[1, 2, 3].map((num) => (
                                            <BootstrapForm.Group className="mb-3" key={num}>
                                                <BootstrapForm.Label className="fw-bold">Prioridad {num}</BootstrapForm.Label>
                                                <Field
                                                    name={`prioridad_${num}`}
                                                    as="select"
                                                    className={`form-select ${touched[`prioridad_${num}`] && errors[`prioridad_${num}`] ? 'is-invalid' : ''}`}
                                                >
                                                    <option value="">Selecciona una opción...</option>
                                                    {opcionesPrioridad.map(opcion => <option key={opcion} value={opcion}>{opcion}</option>)}
                                                </Field>
                                                <ErrorMessage name={`prioridad_${num}`} component="div" className="invalid-feedback" />
                                            </BootstrapForm.Group>
                                        ))}

                                        <BootstrapForm.Group className="mb-3">
                                            <BootstrapForm.Label className="fw-bold">Teléfono de contacto</BootstrapForm.Label>
                                            <Field
                                                name="telefono"
                                                type="text"
                                                placeholder="Ej: 600123456"
                                                className={`form-control ${touched.telefono && errors.telefono ? 'is-invalid' : ''}`}
                                            />
                                            <ErrorMessage name="telefono" component="div" className="invalid-feedback" />
                                        </BootstrapForm.Group>
                                        <BootstrapForm.Group className="mb-3">
                                            <div className="form-check">
                                                <Field
                                                    type="checkbox"
                                                    name="cursado_foundation"
                                                    id="cursado_foundation"
                                                    className={`form-check-input ${touched.cursado_foundation && errors.cursado_foundation ? 'is-invalid' : ''}`}
                                                />
                                                <label htmlFor="cursado_foundation" className="form-check-label fw-bold">
                                                    ¿Has cursado Foundation anteriormente?
                                                </label>
                                                <ErrorMessage name="cursado_foundation" component="div" className="invalid-feedback" />
                                            </div>
                                        </BootstrapForm.Group>

                                        <Button variant="dark" type="submit" className="w-100" disabled={isSubmitting}>
                                            {isSubmitting ? <Spinner size="sm" animation="border" /> : 'Enviar Inscripción'}
                                        </Button>
                                    </FormikForm>
                                )}
                            </Formik>
                        )
                    )}
                </div>
            </Container>
        </>
    );
};

export default Form;